import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import SocketContext from '../../context/SocketContext';
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

const MAX_GUESSES = 6;

function WhirdleGame({ gameState, gamePlayers = [], user = {}, onGameAction , onplayAgain, roomId, isSpectator = false }) {
  const socket = useContext(SocketContext);

  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });
  const inputRef = useRef(null);
  const [guess, setGuess] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  const matchesPerspectivePlayer = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    return (player) => {
      if (!player || idSet.size === 0) return false;
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => idSet.has(String(value)));
    };
  }, [perspectiveIds]);

  const players = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const viewedPlayer = useMemo(() => {
    return players.find(matchesPerspectivePlayer) || null;
  }, [players, matchesPerspectivePlayer]);

  const viewedPlayerSocketId = useMemo(() => {
    if (viewedPlayer?.socketId) return viewedPlayer.socketId;
    if (socket?.id && !isSpectator) return socket.id;
    return perspectiveIds.find(Boolean) || null;
  }, [viewedPlayer?.socketId, socket?.id, isSpectator, perspectiveIds]);

  const { guesses = {}, feedback = {}, finished, winners = [] } = gameState;
  // Always call hooks before any early return
  const myGuesses = useMemo(() => guesses[viewedPlayerSocketId] || [], [guesses, viewedPlayerSocketId]);
  const myFeedback = useMemo(() => feedback[viewedPlayerSocketId] || [], [feedback, viewedPlayerSocketId]);
  // Track which guess row is the latest for animation
  const [flippedRows, setFlippedRows] = useState([]);
  useEffect(() => {
    if (myGuesses.length > 0) {
      const newIdx = myGuesses.length - 1;
      const timeoutId = setTimeout(() => {
        setFlippedRows((prev) => (prev.includes(newIdx) ? prev : [...prev, newIdx]));
      }, 80);
      return () => clearTimeout(timeoutId);
    }
  }, [myGuesses.length]);

  useEffect(() => {
    // When switching perspective, immediately reveal all guesses for the new player
    const guessCount = guesses[viewedPlayerSocketId]?.length || 0;
    setFlippedRows(Array.from({ length: guessCount }, (_, i) => i));
  }, [viewedPlayerSocketId, guesses, feedback]);

  useEffect(() => {
    setGuess("");
    setError("");
    setShake(false);
  }, [viewedPlayerSocketId]);

  // Alphabet coloring logic (must be before any return)
  const alphabet = useMemo(() => Array.from({ length: 26 }, (_, i) => String.fromCharCode(97 + i)), []);
  // Map letter to its best status: 'correct' > 'present' > 'absent'
  const letterStatus = useMemo(() => {
    const status = {};
    myGuesses.forEach((guess, guessIdx) => {
      const feedbackArr = myFeedback[guessIdx] || [];
      guess.split("").forEach((ch, i) => {
        const l = ch.toLowerCase();
        const fb = feedbackArr[i];
        if (!status[l]) {
          status[l] = fb;
        } else {
          // Upgrade status if needed
          if (fb === "correct") status[l] = "correct";
          else if (fb === "present" && status[l] !== "correct") status[l] = "present";
          else if (fb === "absent" && !["correct", "present"].includes(status[l])) status[l] = "absent";
        }
      });
    });
    return status;
  }, [myGuesses, myFeedback]);
  // Alphabet grid: 2 rows of 13, filled left-to-right
  const alphabetRows = useMemo(() => [
    alphabet.slice(0, 13),
    alphabet.slice(13, 26)
  ], [alphabet]);

  // Get other players (exclude self)
  const otherPlayers = useMemo(() => players.filter((player) => !matchesPerspectivePlayer(player)), [players, matchesPerspectivePlayer]);

  // Track which player's guesses are expanded
  const [expandedPlayers, setExpandedPlayers] = useState([]);
  // Helper for rendering a mini grid for a player
  const renderMiniGrid = (playerId) => {
    const feedback = (gameState.feedback && gameState.feedback[playerId]) || [];
    const guesses = (gameState.guesses && gameState.guesses[playerId]) || [];
    const isExpanded = expandedPlayers.includes(playerId);
    // Always render MAX_GUESSES rows, fill with empty if needed
    return (
      <div className="flex flex-col gap-0.5 items-center">
        <div
          className={isGameOver ? "cursor-pointer" : ""}
          onClick={isGameOver ? () => setExpandedPlayers(prev => prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]) : undefined}
          style={isGameOver ? { outline: 'none' } : {}}
        >
          <div
            className="whirdle-grid-anim"
            style={{
              transform: isExpanded ? 'scale(1.15)' : 'scale(1)',
              opacity: isExpanded ? 1 : 0.8,
              zIndex: isExpanded ? 10 : 0,
              transformOrigin: 'top center',
              transition: 'transform 2320ms cubic-bezier(.2,.9,.2,1), opacity 2320ms cubic-bezier(.2,.9,.2,1)'
            }}
          >
        {/* Custom animation for mini grid expand/collapse */}
        <style>{`
          .whirdle-grid-anim {
            will-change: transform, opacity;
          }
        `}</style>
            {Array.from({ length: MAX_GUESSES }).map((_, rowIdx) => {
              if (isExpanded) {
                const guess = guesses[rowIdx] || "";
                return (
                  <div key={rowIdx} className="flex gap-1 justify-center">
                    {Array.from({ length: 5 }).map((_, colIdx) => {
                      const status = feedback[rowIdx]?.[colIdx] || null;
                      let cellClass = "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600";
                      if (status === "correct") cellClass = "bg-green-500 text-black border-green-600";
                      else if (status === "present") cellClass = "bg-yellow-400 text-black border-yellow-500";
                      else if (status === "absent") cellClass = "bg-gray-400 dark:bg-gray-700 text-gray-100 border-gray-500 dark:border-gray-600";
                      const letter = guess[colIdx] ? guess[colIdx].toUpperCase() : "";
                      return (
                        <span
                          key={colIdx}
                          className={"w-8 h-8 flex items-center justify-center rounded font-mono text-lg border " + cellClass}
                          style={{ margin: "2px", textTransform: "uppercase" }}
                        >
                          {letter}
                        </span>
                      );
                    })}
                  </div>
                );
              } else {
                // Default mini grid dots
                return (
                  <div key={rowIdx} className="flex gap-0.5 justify-center">
                    {Array.from({ length: 5 }).map((_, colIdx) => {
                      const status = feedback[rowIdx]?.[colIdx] || null;
                      let dotClass = "bg-gray-200 dark:bg-gray-800";
                      if (status === "correct") dotClass = "bg-green-500";
                      else if (status === "present") dotClass = "bg-yellow-400";
                      else if (status === "absent") dotClass = "bg-gray-400 dark:bg-gray-700";
                      return (
                        <span
                          key={colIdx}
                          className={"inline-block rounded w-2.5 h-2.5 border border-gray-300 dark:border-gray-600 " + dotClass}
                          style={{ margin: "1px" }}
                        ></span>
                      );
                    })}
                  </div>
                );
              }
            })}
          </div>
        </div>
      </div>
    );
  };
  const hasWon = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    if (viewedPlayerSocketId) idSet.add(String(viewedPlayerSocketId));
    return (winners || []).some((winnerId) => idSet.has(String(winnerId)));
  }, [perspectiveIds, viewedPlayerSocketId, winners]);
  const isGameOver = finished;
  const guessesUsedUp = myGuesses.length >= MAX_GUESSES && !hasWon && !isGameOver;

  // Watch for invalid word status in gameState for this user
useEffect(() => {
  if (!gameState || !gameState.invalidWord) return;
  if (viewedPlayerSocketId && gameState.invalidWord[viewedPlayerSocketId]) {
    setError("Not a valid word!");
    setShake(true);
    if (!isSpectator && inputRef.current) inputRef.current.focus();
  }
}, [gameState, viewedPlayerSocketId, isSpectator]);

  if (!gameState) return null;
    
const onInputChange = (e) => {
  setGuess(e.target.value.toLowerCase());
  setError("");
  setShake(false);
};

  const onSubmit = (e) => {
    e.preventDefault();
  if (isSpectator || isGameOver || hasWon) return;
  if (guess.length !== 5) {
    setError("Guess must be 5 letters");
    setShake(true);
    return;
  }
  if (!/^[a-zA-Z]{5}$/.test(guess)) {
    setError("Guess must be 5 letters (A-Z)");
    setShake(true);
    return;
  }
  // Send guess to server
  submitAction({ type: "guess", guess }).catch(()=>{});
  setGuess("");
  };

  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;




  return (
    <div className="w-full max-w-2xl mx-auto flex flex-row items-start gap-6">
      {/* Main game grid and controls */}
      
      <div className="flex-1 flex flex-col items-center">
        {showSpectatorPerspectiveSelector && (
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
            className="max-w-sm self-stretch"
          />
        )}
        
        {/* Alphabet display above input */}
        <div className="mb-2 select-none">
          {alphabetRows.map((row, rowIdx) => (
            <div key={rowIdx} className="flex flex-row gap-1 justify-center mb-0.5">
              {row.map((letter, colIdx) => {
                let colorClass = "bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-700";
                if (letterStatus[letter] === "correct") colorClass = "bg-green-500 text-black border-green-600";
                else if (letterStatus[letter] === "present") colorClass = "bg-yellow-400 text-black border-yellow-500";
                else if (letterStatus[letter] === "absent") colorClass = "bg-red-400 dark:bg-red-700 text-gray-100 border-gray-500 dark:border-gray-600";
                return (
                  <span
                    key={colIdx}
                    className={
                      "w-5 h-5 flex items-center justify-center rounded font-mono text-base border transition-colors duration-200 " +
                      colorClass
                    }
                    style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                  >
                    {letter}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
        
        
        {!isSpectator && !(guessesUsedUp || isGameOver) && (
          <form onSubmit={onSubmit} className="flex gap-2 mb-2">
            <input
              ref={inputRef}
              type="text"
              value={guess}
              onChange={onInputChange}
              disabled={hasWon}
              className={
                "border border-gray-400 dark:border-gray-600 rounded px-2 py-1 text-lg uppercase bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 tracking-widest" +
                (shake ? " animate-shake border-red-600" : "")
              }
              autoFocus
            />
            <button
              type="submit"
              disabled={hasWon || isSpectator}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 rounded font-semibold disabled:bg-gray-400 dark:disabled:bg-gray-700"
            >
              Guess
            </button>
          </form>
        )}
        {guessesUsedUp && (
          <div className="text-center text-sm text-gray-600 dark:text-gray-300 mb-2 font-semibold">Waiting on other players to finish</div>
        )}
        {error && (
          <div className="text-red-600 dark:text-red-400 mb-2 text-sm font-semibold w-full text-center">
            {error}
          </div>
        )}
        <div className="flex flex-col gap-1 w-full">
          {(guesses[viewedPlayerSocketId] || []).map((g, idx) => {
            const status = (feedback[viewedPlayerSocketId] || [])[idx] || null;
            const isFlipped = flippedRows.includes(idx);
            return (
              <div key={idx} className="flex gap-1 justify-center">
                {g.split("").map((ch, i) => {
                  const revealed = !!status && isFlipped;
                  const delayMs = revealed ? i * 90 : 0;
                  let colorClass = "bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700";
                  if (status) {
                    if (status[i] === "correct") colorClass = "bg-green-500 text-gray-800 border-green-600";
                    else if (status[i] === "present") colorClass = "bg-yellow-400 text-gray-800 border-yellow-500";
                    else colorClass = "bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-400 dark:border-gray-600";
                  }
                  return (
                    <span key={i} className="w-10 h-10 flex items-center justify-center rounded font-mono text-xl border whirdle-flip-outer" style={{ textTransform: "uppercase" }}>
                      <span className={"whirdle-flip-inner " + (revealed ? "flipped" : "")} style={{ transitionDelay: `${delayMs}ms` }}>
                        <span className="whirdle-flip-face whirdle-flip-front border text-transparent bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 font-mono">
                          &nbsp;
                        </span>
                        <span className={"whirdle-flip-face whirdle-flip-back " + colorClass}>
                          {ch}
                        </span>
                      </span>
                    </span>
                  );
                })}
              </div>
            );
          })}
          {/* Flip animation styles for letter reveal */}
          <style>{`
            .whirdle-flip-outer { perspective: 800px; }
            .whirdle-flip-inner { transition: transform 600ms cubic-bezier(.2,.9,.2,1); transform-style: preserve-3d; position: relative; width: 100%; height: 100%; }
            .whirdle-flip-inner.flipped { transform: rotateY(180deg); }
            .whirdle-flip-face { backface-visibility: hidden; -webkit-backface-visibility: hidden; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; border-radius: 0.25rem; }
            .whirdle-flip-back { transform: rotateY(180deg); }
          `}</style>
          {/* Fill empty rows */}
          {Array.from({ length: MAX_GUESSES - (guesses[viewedPlayerSocketId]?.length || 0) }).map((_, idx) => (
            <div key={idx} className="flex gap-1 justify-center">
              {Array.from({ length: 5 }).map((_, i) => (
                <span
                  key={i}
                  className="w-10 h-10 flex items-center justify-center rounded font-mono text-xl border bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                ></span>
              ))}
            </div>
          ))}
        </div>
        {/* Mini grid table for other players (now at the bottom) */}
{otherPlayers.length > 0 && (
  <div className="w-full mt-6 flex flex-col items-center">
    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Other Players</div>
    {/* Split into two rows if more than 4 other players */}
    {(() => {
      const maxPerRow = 4;
      const rows = [];
      for (let i = 0; i < otherPlayers.length; i += maxPerRow) {
        rows.push(otherPlayers.slice(i, i + maxPerRow));
      }
      return rows.map((row, rowIdx) => (
        <div key={rowIdx} className="flex flex-row gap-4 justify-center w-full mb-2">
          {row.map((p) => (
            <div key={p.socketId} className="flex flex-col items-center">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 truncate max-w-[60px]" title={p.username}>{p.username}</div>
              {renderMiniGrid(p.socketId)}
            </div>
          ))}
        </div>
      ));
    })()}
  </div>
)}
        {/* {isGameOver && (
          <div className="mt-4 text-lg font-semibold text-green-700 dark:text-green-400">
            Game Over! Word was: <span className="font-mono font-bold">{answer.toUpperCase()}</span>
          </div>
        )}
        {hasWon && (
          <div className="mt-4 text-lg font-semibold text-blue-700 dark:text-blue-300">
            🎉 You solved it!
          </div>
        )} */}
      </div>
    </div>
  );
}

WhirdleGame.Guide = function WhirdleGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Whirdle</h3>
      <ul className="list-disc ml-6">
        <li>Guess the secret 5-letter word in 6 tries.</li>
        <li>Feedback shows which letters are correct or present.</li>
        <li>Compete to solve it in the fewest guesses.</li>
      </ul>
    </div>
  );
};

export default WhirdleGame;
